/**
 * DateFormatter - Utility class for formatting dates and time differences
 */
export class DateFormatter {
    /**
     * Format a future date as a relative time string in French
     * @param {string|Date} dateInput - The future date to format
     * @param {Date} [referenceDate=new Date()] - The reference date (defaults to now)
     * @returns {string|null} Formatted string like "dans 2 heures", "demain", "le 15 mars", or null if invalid
     */
    static formatNextUnlockDate(dateInput, referenceDate = new Date()) {
        if (!dateInput) return null;

        const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
        const diffMs = date - referenceDate;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffHours / 24);

        // If less than 24 hours
        if (diffHours < 24) {
            if (diffHours <= 0) {
                return "dans quelques instants";
            } else if (diffHours === 1) {
                return "dans 1 heure";
            } else {
                return `dans ${diffHours} heures`;
            }
        }

        // If less than 7 days
        if (diffDays < 7) {
            if (diffDays === 1) {
                return "demain";
            } else {
                return `dans ${diffDays} jours`;
            }
        }

        // Otherwise show the date
        return `le ${date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}`;
    }

    /**
     * Calculate the difference in milliseconds between two dates
     * @param {Date} futureDate - The future date
     * @param {Date} [referenceDate=new Date()] - The reference date (defaults to now)
     * @returns {number} Difference in milliseconds
     */
    static getDiffMs(futureDate, referenceDate = new Date()) {
        return futureDate - referenceDate;
    }

    /**
     * Calculate the difference in hours between two dates
     * @param {Date} futureDate - The future date
     * @param {Date} [referenceDate=new Date()] - The reference date (defaults to now)
     * @returns {number} Difference in hours (rounded down)
     */
    static getDiffHours(futureDate, referenceDate = new Date()) {
        const diffMs = this.getDiffMs(futureDate, referenceDate);
        return Math.floor(diffMs / (1000 * 60 * 60));
    }

    /**
     * Calculate the difference in days between two dates
     * @param {Date} futureDate - The future date
     * @param {Date} [referenceDate=new Date()] - The reference date (defaults to now)
     * @returns {number} Difference in days (rounded down)
     */
    static getDiffDays(futureDate, referenceDate = new Date()) {
        const diffHours = this.getDiffHours(futureDate, referenceDate);
        return Math.floor(diffHours / 24);
    }
}
